import { useLazyQuery } from "@apollo/client";
import { getRouteApi, Link } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { SUMMARY_SORT_FIELDS, type SummarySortField } from "../shared/sortFields";
import { transformSummaryTableData, type SummaryTableData } from "../utils/summaryTableData";
import { DataTable } from "@/components/DataTable";
import { useUrlTableSorting } from "@/components/DataTable/useUrlTableSorting";
import { pageInfoFragment, usePagination } from "@/components/Pagination/usePagination";
import { Tooltip } from "@/components/Tooltip";
import { ToastAction } from "@/components/ui/toast";
import { graphql, type VariablesOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatInteger } from "@/lib/utils/formatting";

type SortingField = NonNullable<NonNullable<VariablesOf<typeof destinationsSummaryQuery>["sortBy"]>[0]>["field"];

const routeApi = getRouteApi("/(authed)/_layout/bookings/_tabbed/summary");

interface DestinationsTableProps {
  fromDate: string;
  toDate: string;
}

export function DestinationsTable({ fromDate, toDate }: DestinationsTableProps) {
  const [getDestinations, { loading, data, error, refetch }] = useLazyQuery(destinationsSummaryQuery, {
    fetchPolicy: "cache-and-network",
  });
  const { toast } = useToast();
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const navigate = routeApi.useNavigate();
  const { sortField, sortDesc } = routeApi.useSearch();

  const pagination = usePagination({
    totalCount: data?.supplyChainBookingDestinationsSummary?.connection?.numPartners || 0,
    pageInfo: data?.supplyChainBookingDestinationsSummary?.connection?.pageInfo,
    loading,
  });

  const { sortBy, onSortingChange, sorting } = useUrlTableSorting<SummarySortField>({
    initialSort: { id: "BUSINESS_NAME", desc: false },
    validFields: SUMMARY_SORT_FIELDS,
    sortParams: { sortField, sortDesc },
    onSortParamsChange: (field, desc) => {
      void navigate({ search: (prev) => ({ ...prev, sortField: field, sortDesc: desc }) });
    },
  });

  useEffect(() => {
    const variables: VariablesOf<typeof destinationsSummaryQuery> = {
      businessUnit: activeBusinessUnitId || "",
      fromDate,
      toDate,
      sortBy,
      first: pagination.paginationQueryVariables.first,
      last: pagination.paginationQueryVariables.last,
      after: pagination.paginationQueryVariables.after,
      before: pagination.paginationQueryVariables.before,
    };

    const fetchData = async () => {
      await getDestinations({
        variables,
      });
    };

    fetchData().catch((error: unknown) => {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
        action: (
          <ToastAction onClick={() => refetch()} altText="Try again">
            Try again
          </ToastAction>
        ),
      });
    });
  }, [
    pagination.paginationQueryVariables.first,
    pagination.paginationQueryVariables.last,
    pagination.paginationQueryVariables.after,
    pagination.paginationQueryVariables.before,
    getDestinations,
    toast,
    refetch,
    activeBusinessUnitId,
    fromDate,
    toDate,
    sortBy,
  ]);

  const tableData = transformSummaryTableData(data?.supplyChainBookingDestinationsSummary?.connection?.edges || []);

  return (
    <DataTable
      tableId="destinationsSummaryTable"
      columns={columns}
      data={tableData}
      sortConfig={{ onSortingChange, sorting }}
      loading={loading && !data}
      errorMessage={error?.message}
      pagination={pagination}
    />
  );
}

const { accessor } = createColumnHelper<SummaryTableData>();

const columns = [
  accessor("feedyardName", {
    id: "BUSINESS_NAME" satisfies SortingField,
    header: "Destination",
    cell: (info) => {
      const destinationName = info.getValue();
      const contactId = info.row.original.id;

      return contactId ? (
        <Tooltip label="View contact details" asChild>
          <Link
            to="/contacts/$contactId"
            params={{ contactId }}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 cursor-pointer group hover:text-primary min-w-0"
          >
            <span className="flex-1 min-w-0 truncate">{destinationName}</span>
            <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
          </Link>
        </Tooltip>
      ) : (
        <span className="truncate">{destinationName}</span>
      );
    },
    enableSorting: true,
  }),
  accessor("monthlyCommitment", {
    id: "monthlyCommitment",
    header: "Monthly Commitment",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatInteger(value) : "—";
    },
  }),
  accessor("headCountEntered", {
    id: "NUMBER_OF_HEAD" satisfies SortingField,
    header: "Head Count Entered",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("planned", {
    id: "NUMBER_OF_HEAD_PLANNED" satisfies SortingField,
    header: "Planned",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("booked", {
    id: "NUMBER_OF_HEAD_BOOKED" satisfies SortingField,
    header: "Booked",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("shipped", {
    id: "NUMBER_OF_HEAD_SHIPPED" satisfies SortingField,
    header: "Shipped",
    cell: (info) => formatInteger(info.getValue()),
    enableSorting: true,
  }),
  accessor("percentFilled", {
    id: "percentFilled",
    header: "% Filled",
    cell: (info) => info.getValue(),
  }),
];

export const destinationsSummaryQuery = graphql(
  `
    query DestinationsSummary(
      $businessUnit: ID!
      $fromDate: Date!
      $toDate: Date!
      $sortBy: [SupplyChainBookingSummarySortByField!]
      $first: Int
      $last: Int
      $before: String
      $after: String
    ) {
      supplyChainBookingDestinationsSummary(
        businessUnit: $businessUnit
        fromDate: $fromDate
        toDate: $toDate
        sortBy: $sortBy
      ) {
        connection(first: $first, last: $last, before: $before, after: $after) {
          pageInfo {
            ...PageInfo
          }
          edges {
            node {
              numHead
              numHeadBooked
              numHeadPlanned
              numHeadShipped
              supplyChainPartner {
                businessName
                id
                monthlyTarget
              }
            }
          }
          numPartners
        }
      }
    }
  `,
  [pageInfoFragment],
);
